"use client";

import React, { useState, useEffect } from "react";

import { submitVote } from "./pollApi";
import { ComponentArticlePollPoll } from "@/types/generated";
import { btoaUnicode } from "@/lib/userUtils";

interface PollProps {
  poll?: ComponentArticlePollPoll;
  articleId: string;
  showPoll: boolean | null;
}

export default function PollComp({ poll, articleId, showPoll }: PollProps) {
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [hasVoted, setHasVoted] = useState<boolean>(false);
  const [pollData, setPollData] = useState<PollProps["poll"]>(poll);
  const [newData, setNewData] = useState(null);

  const initialDataLoader = React.useCallback(() => {
    fetch("https://ipwho.is/")
      .then((res) => res.json())
      .then((data) => {
        setNewData(data);
      })
      .catch((err) => console.error("Location error:", err));
  }, []);

  React.useEffect(() => {
    initialDataLoader();
  }, [initialDataLoader]);

  useEffect(() => {
    if (typeof window === "undefined" || !poll?.Question) return;

    const encodedQuestion = btoaUnicode(poll.Question);
    const votedOption = localStorage.getItem(`poll_${encodedQuestion}`);

    if (votedOption !== null) {
      setHasVoted(true);
      setSelectedOption(parseInt(votedOption, 10));
    }
  }, [poll?.id]);

  const handleVote = async (
    optionIndex: number,
    pollOptionId: any,
    newData: any
  ) => {
    if (!hasVoted) {
      const updatedPoll = await submitVote({
        documentId: articleId,
        pollId: Number(poll?.id),
        pollStatus: true,
        pollOptionId: Number(pollOptionId),
        city: newData?.city,
        region: newData?.region,
        country: newData?.country,
        ip: newData?.ip,
      });
      setPollData(updatedPoll?.[0]);
      setSelectedOption(optionIndex);
      setHasVoted(true);

      const encQ = btoaUnicode(poll?.Question!);
      localStorage.setItem(`poll_${encQ}`, optionIndex.toString());
    }
  };

  let totalVotes = pollData?.options?.reduce(
    (sum, option) => sum + Number(option?.vote),
    0
  );

  totalVotes = showPoll ? totalVotes : hasVoted ? 1 : 0;

  return (
    <div className="flex-1 w-full prose lg:prose-base mx-auto max-w-3xl dark:prose-invert p-6 bg-white dark:bg-neutral-900 rounded-md rounded-s-none shadow border-1 border-border border-l-4 border-l-red-700">
      <h2 className="text-base font-semibold mb-4 text-neutral-900 dark:text-neutral-100">
        {pollData?.Question}
      </h2>
      <div className="space-y-2">
        {pollData?.options?.map((option, index) => {
          const votes = showPoll ? option?.vote : hasVoted ? selectedOption === index ? 1 : 0 : 0;
          const percentage =
            totalVotes! > 0
              ? ((votes! / totalVotes!) * 100).toFixed(1)
              : 0;
          return (
            <div key={index} className="relative">
              <button
                className={`w-full text-left p-3 rounded-md flex justify-between items-center gap-4 ${hasVoted
                  ? "bg-neutral-100 dark:bg-neutral-800 cursor-default"
                  : "bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"
                  } ${selectedOption === index
                    ? "ring-2 ring-neutral-400 dark:ring-neutral-300"
                    : ""
                  }`}
                onClick={() => handleVote(index, option?.id, newData)}
                disabled={hasVoted}
              >
                <span className="text-base font-bold text-neutral-900 dark:text-neutral-100">
                  {option?.answer}
                </span>
                {hasVoted && (
                  <span className="ml-2 text-xs font-bold font-sans text-neutral-600 dark:text-neutral-300">
                    ({percentage}%)
                  </span>
                )}
              </button>
              {hasVoted && (
                <div
                  className="absolute top-0 left-0 h-full bg-neutral-500 dark:bg-neutral-400 rounded-md"
                  style={{ width: `${percentage}%`, opacity: 0.4 }}
                />
              )}
            </div>
          );
        })}
      </div>
      {totalVotes && totalVotes > 0 ? (
        <p className="mt-4 text-md text-neutral-900 dark:text-neutral-100">
          Total votes: {totalVotes}
        </p>
      ) : (
        <p className="mt-4 text-md text-neutral-900 dark:text-neutral-100"></p>
      )}
      {!pollData?.pollStatus && (
        <p className="mt-2 text-sm text-red-500 dark:text-red-400">
          This poll is closed
        </p>
      )}
    </div>
  );
}
